import { DateTime, Duration, Interval } from 'luxon';

import { DATE_FORMATS } from './constants';

/**
 * Date utilities for admin interfaces
 */

/**
 * Create DateTime from various input formats
 */
export const createDateTime = (
  input: string | Date | DateTime,
  timezone?: string
): DateTime => {
  let dateTime: DateTime;

  if (input instanceof DateTime) {
    dateTime = input;
  } else if (input instanceof Date) {
    dateTime = DateTime.fromJSDate(input);
  } else {
    dateTime =
      DateTime.fromISO(input) ||
      DateTime.fromSQL(input) ||
      DateTime.fromHTTP(input);
  }

  if (!dateTime.isValid) {
    throw new Error(`Invalid date: ${input}`);
  }

  return timezone ? dateTime.setZone(timezone) : dateTime;
};

/**
 * Format date with various presets
 */
export const formatDateWithPreset = (
  date: string | Date | DateTime,
  preset: keyof typeof DATE_FORMATS,
  timezone?: string
): string => {
  try {
    const dateTime = createDateTime(date, timezone);
    return dateTime.toFormat(DATE_FORMATS[preset]);
  } catch (error) {
    return '—';
  }
};

/**
 * Get relative time from now
 */
export const getRelativeTime = (
  date: string | Date | DateTime,
  timezone?: string
): string => {
  try {
    const dateTime = createDateTime(date, timezone);
    return dateTime.toRelative() || '—';
  } catch (error) {
    return '—';
  }
};

/**
 * Check if date is today
 */
export const isToday = (
  date: string | Date | DateTime,
  timezone?: string
): boolean => {
  try {
    const dateTime = createDateTime(date, timezone);
    const today = DateTime.now().setZone(timezone || DateTime.local().zoneName);
    return dateTime.hasSame(today, 'day');
  } catch (error) {
    return false;
  }
};

/**
 * Check if date is yesterday
 */
export const isYesterday = (
  date: string | Date | DateTime,
  timezone?: string
): boolean => {
  try {
    const dateTime = createDateTime(date, timezone);
    const yesterday = DateTime.now()
      .setZone(timezone || DateTime.local().zoneName)
      .minus({ days: 1 });
    return dateTime.hasSame(yesterday, 'day');
  } catch (error) {
    return false;
  }
};

/**
 * Check if date is within the current week
 */
export const isThisWeek = (
  date: string | Date | DateTime,
  timezone?: string
): boolean => {
  try {
    const dateTime = createDateTime(date, timezone);
    const now = DateTime.now().setZone(timezone || DateTime.local().zoneName);
    return dateTime.hasSame(now, 'week');
  } catch (error) {
    return false;
  }
};

/**
 * Check if date is within the current month
 */
export const isThisMonth = (
  date: string | Date | DateTime,
  timezone?: string
): boolean => {
  try {
    const dateTime = createDateTime(date, timezone);
    const now = DateTime.now().setZone(timezone || DateTime.local().zoneName);
    return dateTime.hasSame(now, 'month');
  } catch (error) {
    return false;
  }
};

/**
 * Get start of day
 */
export const startOfDay = (
  date: string | Date | DateTime,
  timezone?: string
): DateTime => {
  const dateTime = createDateTime(date, timezone);
  return dateTime.startOf('day');
};

/**
 * Get end of day
 */
export const endOfDay = (
  date: string | Date | DateTime,
  timezone?: string
): DateTime => {
  const dateTime = createDateTime(date, timezone);
  return dateTime.endOf('day');
};

/**
 * Get start of week
 */
export const startOfWeek = (
  date: string | Date | DateTime,
  timezone?: string
): DateTime => {
  const dateTime = createDateTime(date, timezone);
  return dateTime.startOf('week');
};

/**
 * Get end of week
 */
export const endOfWeek = (
  date: string | Date | DateTime,
  timezone?: string
): DateTime => {
  const dateTime = createDateTime(date, timezone);
  return dateTime.endOf('week');
};

/**
 * Get start of month
 */
export const startOfMonth = (
  date: string | Date | DateTime,
  timezone?: string
): DateTime => {
  const dateTime = createDateTime(date, timezone);
  return dateTime.startOf('month');
};

/**
 * Get end of month
 */
export const endOfMonth = (
  date: string | Date | DateTime,
  timezone?: string
): DateTime => {
  const dateTime = createDateTime(date, timezone);
  return dateTime.endOf('month');
};

/**
 * Add duration to date
 */
export const addDuration = (
  date: string | Date | DateTime,
  duration: Duration | object,
  timezone?: string
): DateTime => {
  const dateTime = createDateTime(date, timezone);
  const durationObj =
    duration instanceof Duration ? duration : Duration.fromObject(duration);
  return dateTime.plus(durationObj);
};

/**
 * Subtract duration from date
 */
export const subtractDuration = (
  date: string | Date | DateTime,
  duration: Duration | object,
  timezone?: string
): DateTime => {
  const dateTime = createDateTime(date, timezone);
  const durationObj =
    duration instanceof Duration ? duration : Duration.fromObject(duration);
  return dateTime.minus(durationObj);
};

/**
 * Get duration between two dates
 */
export const getDurationBetween = (
  startDate: string | Date | DateTime,
  endDate: string | Date | DateTime,
  timezone?: string
): Duration => {
  const start = createDateTime(startDate, timezone);
  const end = createDateTime(endDate, timezone);
  return end.diff(start);
};

/**
 * Check if date is between two dates
 */
export const isBetween = (
  date: string | Date | DateTime,
  startDate: string | Date | DateTime,
  endDate: string | Date | DateTime,
  timezone?: string
): boolean => {
  try {
    const dateTime = createDateTime(date, timezone);
    const start = createDateTime(startDate, timezone);
    const end = createDateTime(endDate, timezone);

    const interval = Interval.fromDateTimes(start, end);
    return interval.contains(dateTime);
  } catch (error) {
    return false;
  }
};

/**
 * Get business days between two dates (excludes weekends)
 */
export const getBusinessDaysBetween = (
  startDate: string | Date | DateTime,
  endDate: string | Date | DateTime,
  timezone?: string
): number => {
  const start = createDateTime(startDate, timezone);
  const end = createDateTime(endDate, timezone);

  let businessDays = 0;
  let current = start.startOf('day');

  while (current <= end.startOf('day')) {
    // Monday = 1, Sunday = 7
    if (current.weekday >= 1 && current.weekday <= 5) {
      businessDays += 1;
    }
    current = current.plus({ days: 1 });
  }

  return businessDays;
};

/**
 * Get age from birth date
 */
export const getAge = (
  birthDate: string | Date | DateTime,
  timezone?: string
): number => {
  const birth = createDateTime(birthDate, timezone);
  const now = DateTime.now().setZone(timezone || DateTime.local().zoneName);

  return Math.floor(now.diff(birth, 'years').years);
};

/**
 * Parse date range string (e.g., "2023-01-01,2023-12-31")
 */
export const parseDateRange = (
  dateRangeString: string,
  timezone?: string,
  separator = ','
): { start: DateTime; end: DateTime } | null => {
  try {
    const [startStr, endStr] = dateRangeString.split(separator);

    if (!startStr || !endStr) return null;

    const start = createDateTime(startStr.trim(), timezone);
    const end = createDateTime(endStr.trim(), timezone);

    return { start, end };
  } catch (error) {
    return null;
  }
};

/**
 * Format date range
 */
export const formatDateRange = (
  startDate: string | Date | DateTime,
  endDate: string | Date | DateTime,
  options: {
    format?: keyof typeof DATE_FORMATS;
    separator?: string;
    timezone?: string;
  } = {}
): string => {
  const { format = 'MEDIUM', separator = ' - ', timezone } = options;

  try {
    const start = formatDateWithPreset(startDate, format, timezone);
    const end = formatDateWithPreset(endDate, format, timezone);

    return `${start}${separator}${end}`;
  } catch (error) {
    return '—';
  }
};

/**
 * Get common date presets for filters
 */
export const getDatePresets = (timezone?: string) => {
  const now = DateTime.now().setZone(timezone || DateTime.local().zoneName);

  return {
    today: {
      label: 'Today',
      start: now.startOf('day'),
      end: now.endOf('day'),
    },
    yesterday: {
      label: 'Yesterday',
      start: now.minus({ days: 1 }).startOf('day'),
      end: now.minus({ days: 1 }).endOf('day'),
    },
    thisWeek: {
      label: 'This Week',
      start: now.startOf('week'),
      end: now.endOf('week'),
    },
    lastWeek: {
      label: 'Last Week',
      start: now.minus({ weeks: 1 }).startOf('week'),
      end: now.minus({ weeks: 1 }).endOf('week'),
    },
    thisMonth: {
      label: 'This Month',
      start: now.startOf('month'),
      end: now.endOf('month'),
    },
    lastMonth: {
      label: 'Last Month',
      start: now.minus({ months: 1 }).startOf('month'),
      end: now.minus({ months: 1 }).endOf('month'),
    },
    last30Days: {
      label: 'Last 30 Days',
      start: now.minus({ days: 30 }).startOf('day'),
      end: now.endOf('day'),
    },
    last90Days: {
      label: 'Last 90 Days',
      start: now.minus({ days: 90 }).startOf('day'),
      end: now.endOf('day'),
    },
    thisYear: {
      label: 'This Year',
      start: now.startOf('year'),
      end: now.endOf('year'),
    },
    lastYear: {
      label: 'Last Year',
      start: now.minus({ years: 1 }).startOf('year'),
      end: now.minus({ years: 1 }).endOf('year'),
    },
  };
};
